import { useState, useRef, useEffect } from "react";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  BookOpen,
  ClipboardList,
  BarChart2,
  CheckCircle2,
  ChevronDown,
  LayoutList,
  FileDown,
} from "lucide-react";

// ── Option lists ──────────────────────────────────────────────────────────────

const STATES = [
  "ACT", "NSW", "NT", "QLD", "SA", "TAS", "VIC", "WA",
  "Outside Australia / Other",
];

const SCHOOL_TYPES = [
  "Government",
  "Catholic",
  "Independent",
  "Special assistance / flexible learning",
  "Specialist setting",
  "Other",
];

const ROLES = [
  "Classroom teacher",
  "Learning support / wellbeing",
  "Coordinator / head teacher",
  "Leadership",
  "Admin / compliance",
  "Other",
];

const FEATURES = [
  "Teacher diary / daybook",
  "Lesson notes & exports",
  "Student support records",
  "Adjustments & NCCD evidence",
  "Program Registration",
  "AI note polishing",
  "Year-end export & archive",
  "Other",
];


// ── Types ─────────────────────────────────────────────────────────────────────

type FormState = "idle" | "submitting" | "success" | "error";

// ── Select helper ─────────────────────────────────────────────────────────────

function SelectField({
  id,
  label,
  hint,
  value,
  onChange,
  options,
  placeholder = "Select…",
  required,
  error,
}: {
  id: string;
  label: string;
  hint?: string;
  value: string;
  onChange: (v: string) => void;
  options: string[];
  placeholder?: string;
  required?: boolean;
  error?: string;
}) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="text-red-500 ml-0.5">*</span>}
        {hint && <span className="ml-1 font-normal text-gray-400">{hint}</span>}
      </Label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className={`flex h-10 w-full rounded-xl border ${error ? "border-red-400" : "border-input"} bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 text-gray-900`}
      >
        <option value="">{placeholder}</option>
        {options.map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

// ── Page component ────────────────────────────────────────────────────────────

export default function Home() {
  const formRef = useRef<HTMLDivElement>(null);

  // Core identity
  const [name, setName]     = useState("");
  const [email, setEmail]   = useState("");
  const [school, setSchool] = useState("");

  // Segmentation
  const [role, setRole]             = useState("");
  const [stateRegion, setStateRegion] = useState("");
  const [schoolType, setSchoolType] = useState("");

  // Intent
  const [interestedFeature, setInterestedFeature]       = useState("");
  const [currentTool, setCurrentTool]                   = useState("");

  // Consent
  const [marketingConsent, setMarketingConsent] = useState(false);

  // Attribution (silent, captured from URL/referrer)
  const [utmSource, setUtmSource]     = useState("");
  const [utmMedium, setUtmMedium]     = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [utmContent, setUtmContent]   = useState("");
  const [referrer, setReferrer]       = useState("");

  // Form state
  const [formState, setFormState] = useState<FormState>("idle");
  const [errorMsg, setErrorMsg]   = useState("");
  const [formErrors, setFormErrors] = useState<{ feature?: string; consent?: string }>({});

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    setUtmSource(params.get("utm_source") ?? "");
    setUtmMedium(params.get("utm_medium") ?? "");
    setUtmCampaign(params.get("utm_campaign") ?? "");
    setUtmContent(params.get("utm_content") ?? "");
    setReferrer(document.referrer ?? "");
  }, []);

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;

    const errors: { feature?: string; consent?: string } = {};
    if (!interestedFeature) errors.feature = "Please select the feature you're most interested in.";
    if (!marketingConsent) errors.consent = "Please tick the checkbox to receive ClassTrack updates.";
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setFormState("submitting");
    setErrorMsg("");
    try {
      const res = await fetch("/api/waitlist", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          school: school.trim() || undefined,
          role: role || undefined,
          stateRegion: stateRegion || undefined,
          schoolType: schoolType || undefined,
          interestedFeature: interestedFeature || undefined,
          currentTool: currentTool.trim() || undefined,
          marketingConsent,
          utmSource: utmSource || undefined,
          utmMedium: utmMedium || undefined,
          utmCampaign: utmCampaign || undefined,
          utmContent: utmContent || undefined,
          referrer: referrer || undefined,
        }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as any).error ?? "Something went wrong");
      }
      setFormState("success");
    } catch (err: any) {
      setErrorMsg(err.message ?? "Something went wrong. Please try again.");
      setFormState("error");
    }
  }

  return (
    <div className="min-h-[100dvh] flex flex-col bg-white">

      {/* ── Header ──────────────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 px-6 py-3.5 flex items-center border-b border-gray-100 bg-white/95 backdrop-blur-sm">
        <span className="text-xl font-bold tracking-tight text-gray-900">ClassTrack</span>
      </header>

      <main className="flex-1">

        {/* ── Hero ────────────────────────────────────────────────────────── */}
        <section className="relative overflow-hidden bg-gradient-to-b from-emerald-50 via-white to-white pt-16 pb-24 px-6 text-center">
          <div className="absolute inset-0 pointer-events-none">
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-emerald-100/40 rounded-full blur-3xl" />
            <div className="absolute -top-12 -right-24 w-80 h-80 bg-teal-100/30 rounded-full blur-3xl" />
          </div>
          <div className="relative max-w-3xl mx-auto">
            <div className="inline-block bg-emerald-100 text-emerald-800 text-xs font-semibold tracking-wide uppercase rounded-full px-4 py-1.5 mb-6">
              Built for Australian teachers
            </div>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 leading-tight mb-5">
              Your class, clearly documented
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 mb-5 max-w-2xl mx-auto leading-relaxed">
              ClassTrack is the digital daybook that helps you plan your day, record lesson notes, capture support
              evidence, and turn quick observations into dated professional records — without losing your afternoon to admin.
            </p>
            <p className="text-sm text-gray-500 mb-8 max-w-xl mx-auto">
              ClassTrack is preparing to open to Australian teachers for the 2027 school year. Register your
              interest and be the first to know when access becomes available.
            </p>
            <div className="flex justify-center">
              <button
                onClick={scrollToForm}
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground font-semibold rounded-full px-7 py-3 text-sm shadow-md hover:shadow-lg hover:opacity-90 transition-all"
              >
                Register your interest
                <ChevronDown className="w-4 h-4" />
              </button>
            </div>
          </div>
        </section>

        {/* ── Feature cards ────────────────────────────────────────────────── */}
        <section className="py-20 px-6">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-12">
              Everything in one calm, organised space
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {([
                {
                  icon: BookOpen,
                  color: "bg-blue-50 text-blue-600",
                  title: "Plan your day",
                  body: "Set up your timetable, lessons, classes, and tasks. Arrive each morning knowing exactly what's ahead.",
                },
                {
                  icon: ClipboardList,
                  color: "bg-emerald-50 text-emerald-700",
                  title: "Record the day",
                  body: "Capture lesson notes, behaviour, wellbeing, contact notes, and quick observations in seconds — no more relying on memory at the end of the week.",
                },
                {
                  icon: LayoutList,
                  color: "bg-amber-50 text-amber-700",
                  title: "Track your program",
                  body: "Keep planned content, lesson delivery, and program items connected. Build a clear record of what was delivered and when.",
                },
                {
                  icon: FileDown,
                  color: "bg-violet-50 text-violet-700",
                  title: "Export when you need it",
                  body: "Pull diary notes, lesson records, program delivery and support evidence into clean exports when review or reporting time comes.",
                },
              ] as const).map(({ icon: Icon, color, title, body }) => (
                <div key={title} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-7 flex flex-col gap-4">
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
                    <Icon className="w-6 h-6" />
                  </div>
                  <h3 className="font-semibold text-gray-900 text-lg">{title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Evidence & adjustments ──────────────────────────────────────── */}
        <section className="bg-slate-50 py-20 px-6">
          <div className="max-w-4xl mx-auto grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-block bg-white text-emerald-700 text-xs font-semibold tracking-wide uppercase rounded-full px-4 py-1.5 mb-5 border border-emerald-100">
                Your teaching records, in one place
              </div>
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 leading-snug">
                Keep diary, support and program evidence connected
              </h2>
              <p className="text-gray-600 leading-relaxed mb-5">
                ClassTrack is where your lesson notes, student support records, and program delivery all live
                together. Every entry is dated and organised — so you're not digging through scattered notes,
                separate folders, or different systems when review time comes.
              </p>
              <p className="text-gray-600 leading-relaxed">
                Whether it's a quick lesson observation, an adjustment record, or a contact note with a
                parent — it's already written and easy to find when you need it.
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {([
                { label: "Diary / daybook notes", desc: "Day-by-day lesson and class observations, always dated and searchable." },
                { label: "Lesson records", desc: "What you taught, the class or group it was for, and how it went." },
                { label: "Program delivery", desc: "Keep planned and delivered content connected across lessons." },
                { label: "Student support records", desc: "Behaviour, wellbeing, and contact notes — kept in context." },
                { label: "Adjustments & evidence", desc: "Teaching, supplementary, or substantial adjustments with one tap." },
                { label: "Export anytime", desc: "Clean Word or PDF summaries ready for school systems or review." },
              ] as const).map(({ label, desc }) => (
                <div key={label} className="flex items-start gap-3 bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                  <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium text-gray-900 text-sm">{label}</span>
                    <p className="text-xs text-gray-500 mt-0.5">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Register interest form ───────────────────────────────────────── */}
        <section ref={formRef} className="py-20 px-6 bg-white scroll-mt-16">
          <div className="max-w-xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
                Register your interest
              </h2>
              <p className="text-gray-500 text-sm leading-relaxed max-w-sm mx-auto">
                ClassTrack is built for Australian teachers. Tell us a bit about yourself and
                we'll keep you updated as new features launch.
              </p>
            </div>

            {formState === "success" ? (
              <div className="bg-emerald-50 border border-emerald-100 rounded-2xl p-8 text-center">
                <div className="w-14 h-14 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-semibold text-gray-900 text-lg mb-2">Thanks — you're on the list!</h3>
                <p className="text-gray-500 text-sm leading-relaxed">
                  We'll be in touch when ClassTrack becomes available. Keep an eye on your inbox.
                </p>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-white rounded-3xl border border-gray-100 shadow-md p-8 flex flex-col gap-5"
              >
                {/* Name + Email */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="wl-name" className="text-sm font-medium text-gray-700">
                      Name <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="wl-name"
                      type="text"
                      placeholder="Alex Smith"
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      required
                      className="rounded-xl"
                    />
                  </div>
                  <div className="flex flex-col gap-1.5">
                    <Label htmlFor="wl-email" className="text-sm font-medium text-gray-700">
                      Work email <span className="text-red-500">*</span>
                    </Label>
                    <Input
                      id="wl-email"
                      type="email"
                      placeholder="you@school.edu.au"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="rounded-xl"
                    />
                  </div>
                </div>

                {/* School */}
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="wl-school" className="text-sm font-medium text-gray-700">
                    School or organisation
                  </Label>
                  <Input
                    id="wl-school"
                    type="text"
                    placeholder="Northside Public School"
                    value={school}
                    onChange={(e) => setSchool(e.target.value)}
                    className="rounded-xl"
                  />
                </div>

                {/* State + School type */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <SelectField
                    id="wl-state"
                    label="State / Territory"
                    value={stateRegion}
                    onChange={setStateRegion}
                    options={STATES}
                  />
                  <SelectField
                    id="wl-school-type"
                    label="School type"
                    value={schoolType}
                    onChange={setSchoolType}
                    options={SCHOOL_TYPES}
                  />
                </div>

                {/* Role + Feature */}
                <div className="grid sm:grid-cols-2 gap-4">
                  <SelectField
                    id="wl-role"
                    label="Your role"
                    value={role}
                    onChange={setRole}
                    options={ROLES}
                  />
                  <SelectField
                    id="wl-feature"
                    label="Most interested in"
                    value={interestedFeature}
                    onChange={(v) => { setInterestedFeature(v); if (v) setFormErrors((p) => ({ ...p, feature: undefined })); }}
                    options={FEATURES}
                    required
                    error={formErrors.feature}
                  />
                </div>

                {/* Current tool */}
                <div className="flex flex-col gap-1.5">
                  <Label htmlFor="wl-tool" className="text-sm font-medium text-gray-700">
                    What are you currently using to track this?{" "}
                    <span className="font-normal text-gray-400">(optional)</span>
                  </Label>
                  <Input
                    id="wl-tool"
                    type="text"
                    placeholder="e.g. paper folders, spreadsheets, OneNote…"
                    value={currentTool}
                    onChange={(e) => setCurrentTool(e.target.value)}
                    className="rounded-xl"
                  />
                </div>

                {/* Marketing consent */}
                <div className="flex flex-col gap-1">
                  <label className="flex items-start gap-3 cursor-pointer group">
                    <div className="mt-0.5 shrink-0">
                      <input
                        type="checkbox"
                        checked={marketingConsent}
                        onChange={(e) => {
                          setMarketingConsent(e.target.checked);
                          if (e.target.checked) setFormErrors((p) => ({ ...p, consent: undefined }));
                        }}
                        className="rounded accent-primary w-4 h-4"
                      />
                    </div>
                    <span className="text-sm text-gray-600 leading-snug">
                      I'm happy to receive occasional ClassTrack updates and understand I can unsubscribe at any time.{" "}
                      <span className="text-red-500">*</span>{" "}
                      <Link href="/privacy" className="text-primary hover:underline">
                        Privacy policy
                      </Link>.
                    </span>
                  </label>
                  {formErrors.consent && (
                    <p className="text-xs text-red-600 pl-7">{formErrors.consent}</p>
                  )}
                </div>

                {formState === "error" && (
                  <p className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
                    {errorMsg}
                  </p>
                )}

                <Button
                  type="submit"
                  disabled={formState === "submitting" || !name.trim() || !email.trim()}
                  className="w-full rounded-xl py-3 font-semibold"
                >
                  {formState === "submitting" ? "Sending…" : "Register interest"}
                </Button>

                <p className="text-xs text-center text-gray-400">
                  Your information is only used to let you know about ClassTrack — never shared or sold.
                </p>
              </form>
            )}

          </div>
        </section>

        {/* ── Early access positioning ────────────────────────────────────── */}
        <section className="bg-slate-50 py-20 px-6 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
              ClassTrack is being shaped with teachers, not just built for them.
            </h2>
            <p className="text-gray-500 text-base mb-8">
              Early access for teachers who want simpler planning, records and evidence tracking.
            </p>
            <ul className="space-y-3 text-left max-w-md mx-auto mb-8">
              {[
                "Adjustments, behaviour, wellbeing, contact, and lesson records — all in one place",
                "Turn rough notes into professional, dated support records",
                "Identify patterns across students, classes, and support needs",
                "Export records for review, reporting, and school systems",
                "Built by a teacher who understands what busy classrooms actually need",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3 text-gray-700 text-sm">
                  <span className="text-emerald-600 font-bold mt-0.5">✓</span>
                  {item}
                </li>
              ))}
            </ul>
            <div className="flex justify-center">
              <button
                onClick={scrollToForm}
                className="inline-flex items-center justify-center bg-primary text-primary-foreground font-semibold rounded-full px-7 py-3 text-sm shadow-md hover:shadow-lg hover:opacity-90 transition-all"
              >
                Register your interest
              </button>
            </div>
            <p className="mt-5 text-xs text-gray-400">
              Be the first to know when ClassTrack opens for teachers preparing for 2027.
            </p>
          </div>
        </section>

      </main>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="bg-gray-900 text-gray-400 py-10 px-6 text-center text-sm">
        <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-3">
          <Link href="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
          <Link href="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
          <Link href="/ai-privacy" className="hover:text-white transition-colors">AI &amp; Privacy</Link>
          <Link href="/legal/subprocessors" className="hover:text-white transition-colors">Subprocessors</Link>
          <Link href="/pricing" className="hover:text-white transition-colors">Pricing</Link>
        </div>
        <p className="mb-3">&copy; 2026 ClassTrack. All rights reserved.</p>
        <p className="text-xs text-gray-600">
          Existing invited user?{" "}
          <Link href="/sign-in" className="text-gray-400 hover:text-white underline transition-colors">
            Sign in
          </Link>
        </p>
      </footer>
    </div>
  );
}
